<template lang="pug">
	.lexicon--custom-tn-editor.flex-col.space-large.full-width
		Loader(v-if="isLoading", :text="$tCaption('Loading', '@actions') + '..'", :fullscreen="true")
		.flex-row.space.align-center
			.flex-col.flex-1(v-if="lexiconBO")
				.font-small.upper.grey {{$tCaption('Name')}}
				DxTextBox(:value.sync="lexiconBO.Name", valueChangeEvent="input")
			DxButton(:text="$tCaption('Add rule set')", icon="plus", @click="addRuleSet")
		.flex-col.space-large(v-if="lexicon")
			DxTabPanel(:data-source="tabs")
				template(#title="{ data: { ruleSet, idx } }")
					span {{$tCaption('Rule set')}} {{ idx + 1 }}
				template(#item="{ data: { ruleSet, idx } }")
					.flex-col.space-large.tb-padding
						.flex-col
							.flex-row.space.align-center
								.font-small.upper.grey {{ $tCaption('Supported language') }}
								DxButton(icon="close", @click="$confirm(void 0, $tCaption('Remove rule set?')).then(() => ruleSets.splice(idx, 1))")
							DxTagBox(:dataSource="langStore", valueExpr="locale", displayExpr="text", :searchEnabled="true", :value.sync="ruleSet.SupportLanguage", placeholder="", :showClearButton="true")
						.flex-col.space
							.flex-row.space.align-center
								.font-big.upper.grey {{ $tCaption('Rules') }}
								DxButton(icon="plus", @click="addRule(ruleSet)")
							.flex-col.space-medium 
								.rule.flex-col.space(v-for="(rule, ruleIdx) in ruleSet.RuleList")
									.flex-row.align-center.justify
										.flex-row.space.align-center
											.upper.grey {{ $tCaption('Rule') }} {{ ruleIdx + 1 }}
											DxButton(icon="close", @click="$confirm(void 0, $tCaption('Remove rule?')).then(() => ruleSet.RuleList.splice(ruleIdx, 1))")
										a.flex-row.clickable.hoverable(@click="openHelp", :title="$tCaption('Regular expression online tool')")
											fas(icon="far fa-circle-question")
									.flex-col
										.font-small.upper.grey {{ $tCaption('Search pattern') }}
										DxTextBox(:value.sync="rule.SearchPattern", :placeholder="$tCaption('Example') + ': B(\\\\\\\\d+):B(\\\\\\\\d+)'", stylingMode="filled")
									.flex-col
										.font-small.upper.grey {{ $tCaption('Replace pattern') }}
										DxTextBox(:value.sync="rule.ReplacePattern", :placeholder="$tCaption('Example') + ': B$1<say-as interpret-as=\\\\\"characters\\\\\">:</say-as>B$2'", stylingMode="filled")
									.flex-col
										.font-small.upper.grey {{ $tCaption('Comment') }}
										DxTextBox(:value.sync="rule.Comment", stylingMode="filled")
							.flex-row.space.center
								DxButton(:text="$tCaption('Add rule')", icon="plus", @click="addRule(ruleSet)")
</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import axios from 'axios';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import DxSelectBox from 'devextreme-vue/select-box';
	import cognitiveArrays from 'view/insertion/cognitive-arrays';
	import { saveAs } from 'file-saver';
	import DxTagBox from 'devextreme-vue/tag-box';
	import { LEXICON_TYPES } from 'app-core/constants';
	import ArrayStore from 'devextreme/data/array_store';
	import DxButton from 'devextreme-vue/button';
	import DxTabPanel from 'devextreme-vue/tab-panel';

	export default {
		translationCategory: 'lexicon.LexiconEditor',
		name: 'lexicon--editor',
		props: ['value', 'url', 'scrollHeight', 'lexiconName', 'lexiconBO'],
		components: { DxTextBox, Loader, DxScrollView, DxSelectBox, DxTagBox, DxButton, DxTabPanel },
		mixins: [cognitiveArrays],
		data() {
			return {
				lexicon: null,
				lexemes: [],
				isLoading: false,
				lexiconTypes: LEXICON_TYPES.toLocale('name', this)
			};
		},
		computed: {
			ruleSets() {
				return this.lexicon?.RuleSetList || [];
			},
			langStore() {
				return new ArrayStore({
					data: this.languages?.map(f => ({ locale: f.locale.toLowerCase(), text: f.text })) || [],
					key: 'locale'
				});
			},
			tabs() {
				return this.ruleSets.map((f, idx) => ({ idx, ruleSet: f }))
			}
		},
		watch: {
			'url'() {
				this.loadLexicon(true);
			},
			'value'() {
				this.parseLexiconString(this.value);
			},
			'lexemes': {
				handler() {
					this.refreshScroll();
				},
				deep: true
			},
			lexicon: {
				handler() {
					this.emitChange();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.lexiconEditorTN = this;
			if (this.value) {
				this.parseLexiconString(this.value);
			} else {
				this.loadLexicon();
			}
		},
		methods: {
			openHelp() {
				window.open('https://regexr.com', '_blank');
			},
			addRule(ruleSet) {
				ruleSet.RuleList.push({
					SearchPattern: '',
					ReplacePattern: '',
					Comment: ''
				});
			},
			addRuleSet() {
				this.ruleSets.push({
					SupportLanguage: [],
					RuleList: [{
						SearchPattern: '',
						ReplacePattern: '',
						Comment: ''
					}]
				});
			},
			download() {
				saveAs(new Blob([this.lexiconToString()], { type: "application/json;charset=utf-8" }), `${this.lexiconName || 'lexicon'}.json`);
			},
			refreshScroll() {
				this.$nextTick(() => {
					this.$refs.scroll.instance.scrollBy(0.000001);
				});
			},
			loadLexicon(force) {
				if ((!this.lexicon || force) && this.url) {
					this.isLoading++;
					axios.get(this.url + `?timestamp=${this.$Data.uuid()}`).then(r => {
						if (__DEBUG__)
							console.debug('Lexicon loaded', { r });
						this.parseLexicon(r?.data);
					}).finally(() => this.isLoading--);
				} else {
					if (!this.lexicon && !this.url) {
						this.parseLexicon()
					}
				}
			},
			parseLexicon(str) {
				let setDefault = () => this.lexicon = {
					RuleSetList: [
						{
							SupportLanguage: ['en-us'],
							RuleList: [
								{
									SearchPattern: '',
									ReplacePattern: '',
									Comment: ''
								}
							]
						}
					]
				};

				if (str) {
					try {
						this.lexicon = typeof(str) === 'object' ? str : JSON.parse(str);
					} catch {
						setDefault();
					}
				} else {
					setDefault();
				}
			},
			lexiconToString() {
				if (!this.lexicon) return null;
				return JSON.stringify(this.lexicon, void 0, '    ');
			},
			emitChange() {
				if (__DEBUG__)
					console.debug('Lexicon editor change', { lexicon: this.lexicon });
				this.$emit('change', this.lexiconToString(), this.lexicon);
			}
		}
	};
</script>
<style lang='scss'>
	.lexicon--custom-tn-editor {
		.rule {
			padding: 20px;
			background: #f5f5f5;
			border-radius: 4px;
		}
	}
</style>
